import { Panel3D } from 'util/threeDConverter';

import { useMemo } from 'react';

import { OrbitControls, Environment, Grid, PerspectiveCamera } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';

import { GlassPanel } from './GlassPanel';


interface GlassDoorSceneProps {
  panels: Panel3D[];
  materialType: 'black' | 'silver';
  cameraPosition: [number, number, number];
}

function GroundPlane() {
  // Create procedural grass texture using useMemo to avoid recreating on every render
  const grassTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;
    
    // Base green
    ctx.fillStyle = '#6b9654';
    ctx.fillRect(0, 0, 512, 512);
    
    // Add grass-like noise/variation
    for (let i = 0; i < 8000; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const shade = Math.random();
      
      if (shade > 0.8) {
        ctx.fillStyle = '#7aa560'; // Lighter green
      } else if (shade > 0.5) {
        ctx.fillStyle = '#5d8548'; // Darker green
      } else if (shade > 0.2) {
        ctx.fillStyle = '#527a42'; // Even darker
      } else {
        ctx.fillStyle = '#4a6b38'; // Darkest
      }
      
      // Random small shapes to simulate grass blades and variation
      const width = Math.random() * 3 + 1;
      const height = Math.random() * 4 + 1;
      ctx.fillRect(x, y, width, height);
    }
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(50, 50); // Repeat the texture across the plane
    
    return texture;
  }, []);

  return (
    <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]}>
      <planeGeometry args={[2000, 2000]} />
      <meshStandardMaterial 
        map={grassTexture}
        color="#6b9654"
        roughness={1}
        metalness={0}
      />
    </mesh>
  );
}

export function GlassDoorScene({ panels, materialType, cameraPosition }: GlassDoorSceneProps) {
  // Calculate target (center of the design)
  const target: [number, number, number] = panels.length > 0 
    ? [
        panels.reduce((sum, p) => sum + p.position[0], 0) / panels.length,
        panels[0]?.dimensions[1] / 2 || 24,
        panels.reduce((sum, p) => sum + p.position[2], 0) / panels.length,
      ]
    : [0, 24, 0];

  return (
    <Canvas shadows>
      {/* Camera */}
      <PerspectiveCamera makeDefault position={cameraPosition} fov={75} />
      
      {/* Controls */}
      <OrbitControls
        enablePan
        enableZoom
        enableRotate
        minDistance={20}
        maxDistance={400}
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 2 - 0.1}
        target={target}
      />

      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight
        position={[100, 150, 100]}
        intensity={1.2}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-200}
        shadow-camera-right={200}
        shadow-camera-top={200}
        shadow-camera-bottom={-200}
        shadow-camera-far={500}
      />
      <directionalLight position={[-100, 80, -100]} intensity={0.4} />
      <hemisphereLight args={['#87CEEB', '#6B8E23', 0.3]} />

      {/* Environment */}
      <Environment preset="park" />

      {/* Grassy Ground Plane with Texture */}
      <GroundPlane />

      {/* Ground Grid - Subtle overlay */}
      <Grid
        args={[2000, 2000]}
        cellSize={10}
        cellColor="#4a7a2a"
        sectionSize={50}
        sectionColor="#3a6a1a"
        fadeDistance={800}
        fadeStrength={1}
        position={[0, 0, 0]}
        infiniteGrid={true}
      />

      {/* Render all panels */}
      {panels.map((panel, index) => (
        <GlassPanel
          key={index}
          position={panel.position}
          rotation={panel.rotation}
          dimensions={panel.dimensions}
          type={panel.type}
          material={materialType}
        />
      ))}
    </Canvas>
  );
}
